import React from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import * as yup from "yup";

import { PaymentApi, paymentsApi } from "api";
import { PaziaPrefixInput } from "components/atoms/Inputs";
import { PaziaButton, PaziaTextarea } from "components/atoms";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { PaziaModal } from "components/molecules";
import { REFUND_STATUSES } from "consts/payments";
import { getApiErrorMessage, useToaster } from "utils";
import { sumReservedRefunds } from "./refundAvailability";

interface RefundPaymentModalProps {
  onCloseRequest: () => void;
  // Called after the API answered, whatever the outcome, so the list can be reloaded.
  onRefunded: () => void;
  refund: PaymentApi | null;
  refundable: number;
}

export const formatPln = (value: number): string => value.toFixed(2).replace(".", ",");

export const RefundPaymentModal = ({ onCloseRequest, onRefunded, refund, refundable }: RefundPaymentModalProps) => {
  const [inFlight, setInFlight] = React.useState(false);
  // Esc/backdrop must not dismiss the modal while the refund request is being sent.
  const guardedClose = () => {
    if (!inFlight) onCloseRequest();
  };
  return (
    <PaziaModal id="refund-payment-modal" onCloseRequest={guardedClose} isOpen={refund != null} size="xl" className="p-4">
      {refund && (
        <RefundPaymentModalForm
          onCloseRequest={onCloseRequest}
          onRefunded={onRefunded}
          onInFlightChange={setInFlight}
          refund={refund}
          refundable={refundable}
        />
      )}
    </PaziaModal>
  );
};

interface RefundFormValues {
  amount: number;
  note?: string | null;
}

export const RefundPaymentModalForm = ({
  onCloseRequest,
  onRefunded,
  refund,
  refundable,
  onInFlightChange,
}: Omit<RefundPaymentModalProps, "refund"> & { refund: PaymentApi; onInFlightChange?: (inFlight: boolean) => void }) => {
  const { showSuccess, showError, showWarning } = useToaster();
  const [pending, setPending] = React.useState<RefundFormValues | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const schema = yup.object({
    amount: yup
      .number()
      .typeError("Podaj kwotę zwrotu")
      .required("Podaj kwotę zwrotu")
      .moreThan(0, "Kwota zwrotu musi być większa od zera")
      .max(refundable, `Kwota zwrotu nie może być większa niż ${formatPln(refundable)} zł`)
      .test("max-2-decimals", "Kwota może mieć najwyżej 2 miejsca po przecinku", (value) =>
        value == null ? true : Math.abs(value * 100 - Math.round(value * 100)) < 1e-6,
      ),
    note: yup.string().nullable(),
  });
  const methods = useForm<RefundFormValues>({
    resolver: yupResolver(schema) as any,
    defaultValues: { amount: refundable, note: "" },
  });
  const { handleSubmit, watch } = methods;
  const watchedAmount = Number(watch("amount"));
  const buttonAmount = Number.isFinite(watchedAmount) ? formatPln(watchedAmount) : "";

  const student = refund.user ? `${refund.user.firstName} ${refund.user.surname}` : "kursanta";

  const send = async () => {
    if (!pending || submitting) return;
    setSubmitting(true);
    onInFlightChange?.(true);
    try {
      const { data } = await paymentsApi.refund(refund.id, {
        amount: pending.amount,
        note: pending.note?.trim() ? pending.note.trim() : undefined,
      });
      if (data.status === REFUND_STATUSES.Rejected) {
        showError(data.error ?? "Autopay odrzucił zwrot");
      } else if (data.status === REFUND_STATUSES.Requesting) {
        showWarning(data.error ?? "Zwrot czeka na odpowiedź Autopay. Użyj „Ponów”.");
      } else {
        showSuccess("Zwrot zlecony");
      }
      onRefunded();
      onCloseRequest();
    } catch (error) {
      showError(getApiErrorMessage(error, "Wystąpił błąd podczas zlecania zwrotu"));
      // The outcome is unknown (Autopay may have accepted it): never stay armed on the confirmation step.
      setPending(null);
      onRefunded();
      onCloseRequest();
    } finally {
      setSubmitting(false);
      onInFlightChange?.(false);
    }
  };

  if (pending) {
    return (
      <>
        <ModalHeader>Potwierdź zwrot</ModalHeader>
        <p className="mb-4">
          Na pewno zlecić zwrot {formatPln(pending.amount)} zł do {student}? Pieniądze wyjdą z salda Autopay.
        </p>
        <div className="flex justify-end gap-2">
          <PaziaButton type="tertiary" btnType="button" disabled={submitting} onClick={() => setPending(null)}>
            Wróć
          </PaziaButton>
          <PaziaButton btnType="button" processing={submitting} onClick={send}>
            Potwierdzam
          </PaziaButton>
        </div>
      </>
    );
  }

  return (
    <FormProvider {...methods}>
      <ModalHeader>Zwrot płatności</ModalHeader>
      <div className="mb-3 grid grid-cols-3 gap-2 text-sm">
        <div>
          <div className="text-slate-500">Kwota wpłaty</div>
          <div className="font-semibold">{formatPln(Number(refund.amount))} zł</div>
        </div>
        <div>
          <div className="text-slate-500">Zwrócono dotąd</div>
          <div className="font-semibold">{formatPln(sumReservedRefunds(refund))} zł</div>
        </div>
        <div>
          <div className="text-slate-500">Można zwrócić</div>
          <div className="font-semibold">{formatPln(refundable)} zł</div>
        </div>
      </div>
      <form
        onSubmit={handleSubmit((values) => setPending({ amount: Number(values.amount), note: values.note }))}
        className="flex flex-col gap-2"
      >
        <PaziaPrefixInput prefix="PLN" name="amount" type="number" step="0.01" label="Kwota zwrotu" />
        <PaziaTextarea name="note" label="Powód / notatka" />
        <div className="flex justify-end">
          <PaziaButton btnType="submit">{`Zwróć ${buttonAmount} zł`}</PaziaButton>
        </div>
      </form>
    </FormProvider>
  );
};
